import React, { useCallback, useContext, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import { AutoFontSize } from 'auto-fontsize';
import classNames from 'classnames';

import quizInstanceStatusContext from '../context/quizInstanceStatusContext';
import Status from '../enums/QuizInstanceStatus';
import { isImageMimeType } from '../utils';
import checkmarkImgSrc from '../img/bock.png';

export default function QuizQuestion(props) {

  const {
    color,
    question,
    questionNumber,
    localAnswerMap,
    answerIndexMap,
    onAnswerChoiceToggled,
    onAnswerTextUpdated,
    swipeModeEnabled = false,
    hideAudio = false } = props;

  question.answerList.sort((a, b) => 
  {
    const aIdx = answerIndexMap[a.id];
    const bIdx = answerIndexMap[b.id];

    return aIdx < bIdx ? -1 : 1;
  });

  const ctx = useContext(quizInstanceStatusContext);
  const isReviewMode = ctx.status === Status.Review || ctx.quizInfo.endsAt <= new Date();

  const FREE_TEXT_MAX_LENGTH = 2048;
  const [freeTextLength, setFreeTextLength] = useState(0);

  // Compute inlined styles using color palettes from backend
  const getAnswerDisplayConf = useCallback((question, answer) => {

    const inlineStyle = { backgroundColor: color.primaryCss };
    const classes = { participantReviewMode: isReviewMode };

    let selected = false;
    let correct = false;

    if (isReviewMode)
    {
      selected = 
        ctx.quizInfo.participantAnswerList 
     && ctx.quizInfo.participantAnswerList.find(a => a && a.id === answer.id);

      correct = ctx.quizInfo.correctAnswerList 
        && ctx.quizInfo.correctAnswerList.find(a => a && a.id === answer.id);

      if (selected)
      {
        classes.selectedAnswer = true;
      }

      if (correct)
      {
        classes.correctAnswer = true;
      }
    }

    // Quiz is active, mark selected answer based on local session
    else if (localAnswerMap[question.id] && localAnswerMap[question.id].indexOf(answer.id) > -1)
    {
      classes.selectedAnswer = true;
      inlineStyle.color = color.primaryCss;
    }

    return { inlineStyle, classes, selected, correct };

  }, [color, localAnswerMap, ctx, isReviewMode]);

  // Collect uploaded image URLs
  let allImageUrls = [];
  if (question.mediaList && question.mediaList.length > 0)
  {
    allImageUrls = allImageUrls.concat(
      question.mediaList
        .filter(m => isImageMimeType(m.mimeType))
        .map(m => `/api/quiz/questionMedia/${m.id}`));
  }

  if (question.externalMediaList && question.externalMediaList.length > 0) 
  {
    allImageUrls = allImageUrls.concat(
      question.externalMediaList
        .filter(m => isImageMimeType(m.mimeType))
        .map(m => m.url));
  }

  // NY: Collect external audio tracks (Deezer)
  const audioTrackList = (question.externalMediaList || [])
    .filter(m => m.mimeType === "audio/mpeg" || (m.mimeType || "").startsWith("audio/"));

  const isMusicQuiz = ctx.quizInfo.type === "Musicquiz";

  return (
    <div className={classNames("quizQuestion mb-2", { participantReviewMode: isReviewMode})} style={{ backgroundColor: color.primaryCss }}>
      <div className={classNames("quizQuestionNumber", { participantReviewMode: isReviewMode})} style={{ color: color.primaryCss }}>
        {questionNumber}
      </div>
      {swipeModeEnabled && <div className="quizQuestionNextIndicator">
        <span>Swajpa!</span>
        <i className="fa fa-2x fa-angle-double-right bounce"></i>  
      </div>}
      <h3 className="quizQuestionText">{question.markdown}</h3>

      {/* Bildgalleri (uppladdade bilder eller externa bilder) */}
      {allImageUrls.length > 0 ?
        <div>
          <ImageGallery 
            slideDuration={250}
            disableSwipe={true}
            showPlayButton={false} 
            showFullscreenButton={false}
            items={allImageUrls.map(url => ({ original: url, originalHeight: "400px" }))} />
        </div>
        : null
      }

      {/* NY: Musikspelare för Deezer-låtar */}
      {isMusicQuiz && audioTrackList.length > 0 && audioTrackList.map((track, idx) => (
        <div key={idx} className="quizMusicPlayer">

          {/* Albumomslag + metadata — visas i review-läge (även i samlat läge) */}
          {isReviewMode && track.cover && (
            <div className="quizMusicPlayerMeta">
              <img
                src={track.cover}
                alt={track.album || ""}
                className="quizMusicPlayerCover"
              />
              <div className="quizMusicPlayerInfo">
                <div className="quizMusicPlayerTitle">{track.title}</div>
                <div className="quizMusicPlayerArtist">{track.artist}</div>
                <div className="quizMusicPlayerAlbum">{track.album}</div>
              </div>
            </div>
          )}

          {/* Ljudspelaren — döljs i samlat läge */}
          {!hideAudio && (
            <audio
              controls
              src={track.url}
              className="quizMusicPlayerAudio"
              controlsList="nodownload"
            >
              Din webbläsare stöder inte ljuduppspelning.
            </audio>
          )}

        </div>
      ))}

      {(ctx.quizInfo.type === "Quiz" || ctx.quizInfo.type === "Poll" || isMusicQuiz) && question.config && question.config.inputMode &&
        <div className="quizAnswerInputWrapper">
          {!isReviewMode &&
            <input
              type="text"
              className="form-control"
              maxLength={2048}
              onChange={ev => onAnswerTextUpdated(question, ev.target.value)}
              placeholder="Skriv ditt svar här."
            />
          }
          {isReviewMode &&
            <div className="quizAnswerInputReview">
              {(() => {
                const submitted = (ctx.quizInfo.participantAnswerList || [])
                  .find(a => a && a.questionId === question.id);
                if (!submitted) {
                  return <div>Inget svar skickades in.</div>;
                }
                const correct = submitted.correct === true;
                const wrong = submitted.correct === false;
                return (
                  <div>
                    Ditt svar: <strong>{submitted.text || ""}</strong>
                    {correct && <span style={{marginLeft: "8px"}}>✓ Rätt!</span>}
                    {wrong && <span style={{marginLeft: "8px"}}>✗ Fel</span>}
                  </div>
                );
              })()}
            </div>
          }
        </div>
      }

      {(ctx.quizInfo.type === "Quiz" || ctx.quizInfo.type === "Poll" || isMusicQuiz) && !(question.config && question.config.inputMode) &&
        <div className="quizAnswerChoiceWrapper">
          {question.answerList.map(answer =>
            {
              const answerDisplayConf = getAnswerDisplayConf(question, answer);
              return <div
                key={answer.id}
                style={answerDisplayConf.inlineStyle}
                className={classNames("quizAnswer", answerDisplayConf.classes)}
                onClick={() => onAnswerChoiceToggled(question, answer)}
              >
                <div>
                  <AutoFontSize
                    text={answer.text}
                    minTextSize={10}
                    textSize={15}
                    textSizeStep={2}
                    targetLines={2}
                  />
                </div>
                {answerDisplayConf.correct && <img src={checkmarkImgSrc} alt="" />}
              </div>
            }
          )}
        </div>
      }

      {ctx.quizInfo.type === "Free" &&
        <div className="quizAnswerFreetextWrapper">
          {!isReviewMode &&
            <>
              <textarea
                maxLength={FREE_TEXT_MAX_LENGTH}
                onChange={ev => {
                  setFreeTextLength(ev.target.value.length);
                  onAnswerTextUpdated(question, ev.target.value);
                }}
                placeholder="Skriv ditt svar här. Du kan skicka in flera gånger.">
              </textarea>
              <div
                className="quizFreeTextCounter"
                style={{
                  color: freeTextLength >= FREE_TEXT_MAX_LENGTH * 0.9 ? "#c0392b" : "#fff",
                  textAlign: "right",
                  fontSize: "smaller",
                  paddingRight: "4px"
                }}>
                {freeTextLength}/{FREE_TEXT_MAX_LENGTH}
              </div>
            </>
          }
        </div>
      }

      {/* Feedback — visas i review-läge om den finns */}
      {isReviewMode && question.feedbackMarkdown && (
        <div className="quizQuestionFeedback mt-2 p-2">
          <strong>Feedback:</strong> {question.feedbackMarkdown}
        </div>
      )}

    </div>
  );
}